//@flow

import React, { createRef, useCallback, useState } from "react";
import Form from "react-bootstrap/Form";
import ReCAPTCHA from "react-google-recaptcha";

import {ActionButton, CountrySelect, HubspotFormMessage, PromiseWatcher} from "@/components";
import {HubspotForm} from "@/utils";

type ContactFormData = {
    firstName?: string,
    lastName?: string,
    email?: string,
    message?: string,
    token?: string,
    [string]: string,
};

type Props = {
    portalId: string,
    formId: string,
};

const FormInitialState = {
    firstName: "",
    lastName: "",
    email: "",
    message: "",
    country: "",
    company: "",
    token: "",
};

const ContactForm = ({portalId, formId}: Props): React$Element<typeof PromiseWatcher> => {
    const hsForm = new HubspotForm<ContactFormData>(portalId, formId, {
        firstName: "firstname",
        lastName: "lastname",
    });

    const [promise, setPromise] = useState<Promise<any> | null>(null);

    const [form, setForm] = useState<ContactFormData>(FormInitialState);

    const recaptchaRef = createRef<typeof ReCAPTCHA>();

    const onChange = (event: {target: {name: string, value: string}}) => {
        const { name, value } = event.target;
        setForm(form => ({
            ...form,
            [name]: value,
        }));
    };

    const onSubmit = (event: Event) => {
        event.preventDefault();

        recaptchaRef.current?.executeAsync().then(() => {
            setPromise(hsForm.submit(form));
        });
    };

    // Set the initial form state on successful submit.
    promise
        ?.then(() => setForm(FormInitialState))
        .catch(() => {
            /* Ignored */
        });

    const onVerify = useCallback((token: string) => {
        setForm(form => ({
            ...form,
            token,
        }));
    }, []);

    return (
        <PromiseWatcher promise={promise}>
            {value => (
                <Form
                    method="POST"
                    onSubmitCapture={onSubmit}
                    className={value.type}
                >
                    <Form.Group>
                        <Form.Control
                            type="text"
                            name="firstName"
                            placeholder="First name"
                            value={form.firstName}
                            onChange={onChange}
                        />
                        <HubspotFormMessage
                            promise={promise}
                            field="firstname"
                        />
                    </Form.Group>

                    <Form.Group>
                        <Form.Control
                            type="text"
                            name="lastName"
                            placeholder="Last name"
                            value={form.lastName}
                            onChange={onChange}
                        />
                        <HubspotFormMessage
                            promise={promise}
                            field="lastname"
                        />
                    </Form.Group>

                    <Form.Group>
                        <Form.Control
                            type="email"
                            name="email"
                            placeholder="Email"
                            value={form.email}
                            onChange={onChange}
                        />
                        <HubspotFormMessage promise={promise} field="email" />
                    </Form.Group>

                    <Form.Group>
                        <Form.Control
                            type="text"
                            name="company"
                            placeholder="Company"
                            value={form.company}
                            onChange={onChange}
                        />
                        <HubspotFormMessage promise={promise} field="company" />
                    </Form.Group>

                    <Form.Group>
                        <CountrySelect
                            name="country"
                            placeholder="Select country"
                            value={form.country}
                            onChange={onChange}
                        />
                        <HubspotFormMessage promise={promise} field="country" />
                    </Form.Group>

                    <Form.Group>
                        <Form.Control
                            as="textarea"
                            type="text"
                            name="message"
                            placeholder="Message"
                            value={form.message}
                            onChange={onChange}
                        />
                        <HubspotFormMessage promise={promise} field="message" />
                    </Form.Group>

                    <Form.Group>
                        <PromiseWatcher promise={promise}>
                            {value => (
                                <>
                                    <div>
                                        <ReCAPTCHA
                                            sitekey="6LfDN50iAAAAAKxlsQ81Gi9Y0WEip_xjw8i3peXD"
                                            size="invisible"
                                            onChange={onVerify}
                                            ref={recaptchaRef}
                                        />
                                    </div>

                                    <ActionButton
                                        variant="dark"
                                        state={value.type}
                                        submit={true}
                                    >
                                        {value.type === "success"
                                            ? "Thank you"
                                            : "Send message"}
                                    </ActionButton>
                                </>
                            )}
                        </PromiseWatcher>
                    </Form.Group>
                </Form>
            )}
        </PromiseWatcher>
    );
};

export default ContactForm;
